import { InsuranceType } from 'domains/signup/data/types';
import { NOT_ACCEPTED_INSURANCE_PROVIDER_OPTION } from 'domains/signup/insurance/data/insurance';
import { MachineStateType } from 'utils/xstate/types';
import { assign, setup } from 'xstate';

export type InsuranceMachineInput = {
  state: string;
};

export type CompletedInsuranceMachineContext = {
  state: string;
  useInsurance: boolean;
  insuranceType: InsuranceType;
  medicaidProvider: string;
};

export type InsuranceResult = {
  useInsurance: boolean;
  insuranceType: InsuranceType;
  medicaidProvider: string;
};

/** @xstate-layout N4IgpgJg5mDOIC5QEsB2sCuAnAhqgxmALI74AWaYAdGKgC5hYCqsYAkutnoQMSYBGAW2R0W7TrgJgA2gAYAuolAAHAPawRyVaiUgAHogC0AJgCMADiqnTAdmOyALADZZxgMyzHbgDQgAnkYArDZUDqZOTjYOssFODsZxAL6JvmiYkoQk5JQ09IxiHOncYHwYQiIFEsXSpopIIGoadFo69QYINlFUbjaBstYOg7IAnKbDvgEIhsGh4ZHRsfFJKSBpXFJZFKjUtAxYheuEACp+yiUCwnQHGWAnZ3J1Kuqa2rrtJmNUgcM9Dh7DwwcgVMgwmiB6xio5lMfXMw3M5g8bnCgWSqSqG1IWx2eX2GOOp3OZUu12KdxktV0jRerVA7TcM0CTmMgUCbgWNmZsicYIQEKhMNkcIRSNsaNW+OIWJyu0YpKk5J4-FIAGsHlTns1Xm0jECQoFnIjzGF3KMnIFeYZ2VQnMN+uzHPZzJzTOK1jdNjLcURIMh8DhkBAAApYVQAN0DjFK5ToPogfoDwdDEYgjHV9WpWtp+iMCX1P057hhMKcPn8iHMIxtQPsNnZwycplcbslnu2uT2cYTgZD4cjWGjly7-p7yf7NUeDU1LTeRnNIVG-VZ5mMxhs-V50Mhjlt9iLw2+5hbRUx2Xbsqww8TvZTUeV+DVCg1TRnOqmeq+hrcxtMppRvLcRsqBsM0okbNwDwSBxj0OKUzxxTtfRHJM+1TAdBCQ68xzQgBRLBQywdMnhfbU6UQcJhm6b5jAbEUojrMtJm+BwvgZCDKxZO1Ihgj1pXbd1igAOVUK5UEEsA6AAd1ULAVT4chIAwAAbMAAHURDIENkDDHAGCDHA-CIqcSOzd5vkCKgYnMb5SzCb84V5awDUs6xbVMZEQQSI9xVQVRU3geoBNPbFnxpWcpnXCzFxhQIVzXDdyymetLLCOIGIZVxoJWILMj4hD8lYeVCFCrNwsMdzIQBKrquqy0ElMT85jraFhlXLL0RPXL4I7OVJXJErXzIqZ3KcUJOWCX8IgiTwbEcwFZmNLlHHXJt2olTq4OxHrL0w0dUMYAbSJzYbnGArdGybfpzEbXlGxCeqD0BCJ2WWDrYLbagcrAABhVRBGUFSGEO7M6FUZRyJhSEXXXetf2sXwVIAMzoIxwhYswHGNHo+k8TweRAfgRNBwRyPsWRgLu2RYbMUxfCwZAoDIFH33RkEsd6XG8d8STAzoMhSdkXwyDABmmcQTHfHeNGqAx9mcdx-HJicqHKep6weOKD6qC+4TRPEqSZJVYGyu+cmwJGRsXGNRz+khYxBgcWjATreJkmSIA */
export const insuranceMachine = setup({
  types: {
    context: {} as Partial<CompletedInsuranceMachineContext>,
    events: {} as
      | { type: 'back' }
      | { type: 'submitUseInsurance'; useInsurance: boolean }
      | { type: 'submitInsuranceType'; insuranceType: InsuranceType }
      | { type: 'submitMedicaidProvider'; medicaidProvider: string }
      | { type: 'scheduleWithPrivatePay' }
      | { type: 'medicaidProviderError' },

    input: {} as InsuranceMachineInput,

    output: {} as InsuranceResult,
  },

  actions: {
    saveUseInsurance: assign((_, params: { useInsurance: boolean }) => ({
      useInsurance: params.useInsurance,
    })),
    saveInsuranceType: assign((_, params: { insuranceType: InsuranceType }) => ({
      insuranceType: params.insuranceType,
    })),
    saveMedicaidProvider: assign((_, params: { medicaidProvider: string }) => ({
      medicaidProvider: params.medicaidProvider,
    })),
    disableUseInsurance: assign(() => ({
      useInsurance: false,
    })),
  },
  guards: {
    isUsingInsurance: (_, params: { useInsurance: boolean }) => params.useInsurance,
    isUsingMedicaid: (_, params: { insuranceType: InsuranceType }) => params.insuranceType === 'medicaid',
    medicaidProviderIsInNetwork: (_, params: { medicaidProvider: string }) =>
      params.medicaidProvider !== NOT_ACCEPTED_INSURANCE_PROVIDER_OPTION.value,
  },
}).createMachine({
  id: 'insuranceMachine',

  initial: 'enterUseInsurance',

  context: ({ input }) => ({
    state: input.state,
  }),

  states: {
    enterUseInsurance: {
      on: {
        submitUseInsurance: [
          {
            target: 'enterInsuranceType',
            guard: {
              type: 'isUsingInsurance',
              params: ({ event }) => event,
            },

            actions: {
              type: 'saveUseInsurance',
              params: ({ event }) => event,
            },
          },
          {
            target: 'insuranceComplete',
            actions: {
              type: 'saveUseInsurance',
              params: ({ event }) => event,
            },
          },
        ],
      },
    },

    enterInsuranceType: {
      on: {
        submitInsuranceType: [
          {
            target: 'enterMedicaidProvider',
            guard: {
              type: 'isUsingMedicaid',
              params: ({ event }) => event,
            },
            actions: {
              type: 'saveInsuranceType',
              params: ({ event }) => event,
            },
          },
          {
            target: 'insuranceComplete',
            actions: {
              type: 'saveInsuranceType',
              params: ({ event }) => event,
            },
          },
        ],

        back: 'enterUseInsurance',
      },
    },

    enterMedicaidProvider: {
      on: {
        submitMedicaidProvider: [
          {
            target: 'insuranceComplete',
            guard: {
              type: 'medicaidProviderIsInNetwork',
              params: ({ event }) => event,
            },

            actions: {
              type: 'saveMedicaidProvider',
              params: ({ event }) => event,
            },
          },
          {
            target: 'insuranceNotInNetwork',
            actions: [
              {
                type: 'saveMedicaidProvider',
                params: ({ event }) => event,
              },
              { type: 'disableUseInsurance' },
            ],
          },
        ],

        back: 'enterInsuranceType',
        medicaidProviderError: {
          target: 'insuranceComplete',
          actions: 'disableUseInsurance',
        },
      },
    },

    insuranceComplete: {
      type: 'final',
    },

    insuranceNotInNetwork: {
      on: {
        scheduleWithPrivatePay: 'insuranceComplete',
      },
    },
  },

  output: ({ context }) => {
    const { insuranceType, medicaidProvider, useInsurance } = context as CompletedInsuranceMachineContext;
    const result: InsuranceResult = {
      insuranceType,
      medicaidProvider,
      useInsurance,
    };

    return result;
  },
});

export type InsuranceEntryState = MachineStateType<typeof insuranceMachine>;
